import { PROTOCOL } from './constants';
import type { Condition } from './enums';

/** Stem diameter from circumference. The database keeps the same formula as a generated column. */
export function diameterFromGirthCm(girthCm: number): number {
  if (!Number.isFinite(girthCm) || girthCm < 0) throw new RangeError(`girth must be a non-negative number, got ${girthCm}`);
  return girthCm / Math.PI;
}

/** Once a tree is taller than the trigger height, the DBH girth (130 cm) is required as well as the basal one. */
export function requiresDbhGirth(heightCm: number): boolean {
  return heightCm > PROTOCOL.DBH_TRIGGER_HEIGHT_CM;
}

export interface ConditionCounts {
  H: number;
  S: number;
  D: number;
  NF: number;
}

export function countConditions(conditions: readonly Condition[]): ConditionCounts {
  const c: ConditionCounts = { H: 0, S: 0, D: 0, NF: 0 };
  for (const k of conditions) c[k] += 1;
  return c;
}

export interface SurvivalSummary {
  /** A on the worksheet: every tree the team found (H + S + D). NF is not a found tree. */
  found: number;
  /** B on the worksheet: alive = healthy + stressed. */
  alive: number;
  /** (B / A) x 100, rounded to one decimal; null when nothing was found. */
  survivalRatePct: number | null;
  notFound: number;
}

/**
 * The worksheet's section 2, exactly:
 *   A = total trees found (Alive + Stressed + Dead)
 *   B = total trees ALIVE (Alive + Stressed)
 *   Survival rate = (B / A) x 100
 * NF trees are reported separately; they are neither alive nor dead until the
 * NOT_FOUND_SURVEYS_TO_DEAD rule closes them.
 */
export function survivalRate(counts: ConditionCounts): SurvivalSummary {
  const found = counts.H + counts.S + counts.D;
  const alive = counts.H + counts.S;
  const survivalRatePct = found === 0 ? null : Math.round((alive / found) * 1000) / 10;
  return { found, alive, survivalRatePct, notFound: counts.NF };
}

/**
 * Cumulative survival since planting for a zone: planted trees are the denominator,
 * so a tree that vanished is a loss rather than a silently smaller sample.
 */
export function cumulativeSurvivalPct(planted: number, aliveNow: number): number | null {
  if (planted <= 0) return null;
  return Math.round((aliveNow / planted) * 1000) / 10;
}

/**
 * Tree status after a survey, given the sequence of conditions recorded for it
 * (oldest first). NF on NOT_FOUND_SURVEYS_TO_DEAD consecutive surveys becomes dead.
 */
export function statusFromHistory(conditions: readonly Condition[]): 'active' | 'dead' | 'not_found' {
  if (conditions.length === 0) return 'active';
  const last = conditions[conditions.length - 1];
  if (last === 'D') return 'dead';
  if (last === 'NF') {
    let run = 0;
    for (let i = conditions.length - 1; i >= 0 && conditions[i] === 'NF'; i--) run += 1;
    return run >= PROTOCOL.NOT_FOUND_SURVEYS_TO_DEAD ? 'dead' : 'not_found';
  }
  return 'active';
}

/** True when the last N surveys were all S — the dashboard's "stressed twice in a row" flag. */
export function stressedFlag(conditions: readonly Condition[]): boolean {
  const n = PROTOCOL.STRESSED_SURVEYS_TO_FLAG;
  if (conditions.length < n) return false;
  return conditions.slice(-n).every((c) => c === 'S');
}
